import { isStripe } from "@/lib/payment";
import type { StripeError } from "@stripe/stripe-js";

import type {
  StripeFailure,
  StripePaymentFormRef
} from "../components/StripePaymentForm";
import {
  manualGateway,
  PaymentFailedError,
  type PaymentGateway,
  type SelectedGateway
} from "./gateway";

/** Stripe error types whose message is written for the buyer. */
const BUYER_FACING_STRIPE_TYPES: ReadonlySet<StripeError["type"]> = new Set([
  "card_error",
  "validation_error"
]);

/**
 * Stripe error codes that arrive under another type but are still the buyer's
 * to act on. A failed 3-D Secure challenge is an `invalid_request_error` whose
 * message asks the buyer to choose a different payment method.
 */
const BUYER_FACING_STRIPE_CODES: ReadonlySet<string> = new Set([
  "payment_intent_authentication_failure"
]);

/**
 * A card decline or a field the buyer can fix is a `PaymentFailedError`, shown
 * as-is. Anything else (Stripe not loaded, `api_error`, `rate_limit_error`,
 * `api_connection_error`, no type at all) is a plain `Error`, so the checkout
 * shows friendly copy and reports it.
 */
function toGatewayError(failure: StripeFailure, fallback: string): Error {
  const message = failure.error || fallback;
  const buyerFacing =
    (failure.errorType !== undefined &&
      BUYER_FACING_STRIPE_TYPES.has(failure.errorType)) ||
    (failure.errorCode !== undefined &&
      BUYER_FACING_STRIPE_CODES.has(failure.errorCode));
  return buyerFacing ? new PaymentFailedError(message) : new Error(message);
}

/**
 * Adapts the imperative Stripe form ref (which owns the live `<Elements>`
 * context) to the {@link PaymentGateway} seam. `prepare` runs the
 * submit-then-createPaymentMethod step that must stay in the click handler;
 * `confirm` settles one PaymentIntent with its pre-created PaymentMethod.
 */
export function createStripeGateway(ref: StripePaymentFormRef): PaymentGateway {
  return {
    prepare: async (count) => {
      const result = await ref.submitAndCreatePMs(count);
      if (!result.success) {
        throw toGatewayError(result, "Please check your payment details");
      }
      return (result.paymentMethodIds ?? []).map((id) => ({
        paymentMethodId: id
      }));
    },
    confirm: async (clientSecret, instrument) => {
      const result = await ref.confirmCardPayment(
        clientSecret ?? "",
        instrument.paymentMethodId
      );
      if (!result.success) {
        throw toGatewayError(result, "Payment confirmation failed");
      }
    }
  };
}

/**
 * Selects the payment gateway for a provider - the one place the provider-kind
 * check lives, replacing the scattered `isStripe` / `isManual` branches in the
 * checkout orchestration.
 *
 * Only Stripe Connect (`pp_card_stripe-connect`) needs client-side
 * prepare/confirm, so it gets the {@link createStripeGateway} adapter (and
 * requires the mounted form ref). **Every other provider** - manual
 * pay-on-delivery, bank transfer, and any future off-platform method - settles
 * its payment outside the browser and needs no client-side confirmation, so it
 * uses the no-op {@link manualGateway}. The backend only ever offers configured
 * providers, so there is no "unknown provider" to reject here.
 *
 * Returns the gateway paired with the provider it was built for - see
 * {@link SelectedGateway}. This is the only place that pairing is minted, so
 * downstream code cannot accidentally settle one provider's sessions with
 * another's gateway by passing the two around separately.
 */
export function gatewayFor(
  providerId: string,
  deps: { stripeRef: StripePaymentFormRef | undefined }
): SelectedGateway {
  if (isStripe(providerId)) {
    if (!deps.stripeRef) {
      throw new Error("Payment form not ready");
    }
    return { providerId, gateway: createStripeGateway(deps.stripeRef) };
  }
  return { providerId, gateway: manualGateway };
}
